import type { TypedDocumentNode } from "@apollo/client";
import gql from "graphql-tag";

type RetailerSummary = {
  retailer_id: string
  name: string
  short_name: string
  from_warehouse_id: number | null
  to_warehouse_id: number | null
  created_at: string
  updated_at: string
  deleted_at?: string | null
  media: {
    file_path: string
  } | null
  from_warehouse_ns_bin_id: string | null
  distribution_ns_class_id: string | null
};

export type GetRetailersListQueryVariables = {
  status: string
};

export type GetRetailersListQuery = {
  retailers: RetailerSummary[]
};

export const GET_RETAILERS_LIST: TypedDocumentNode<
  GetRetailersListQuery,
  GetRetailersListQueryVariables
> = gql`
  query retailers($status: String!) {
    retailers(status: $status){
      retailer_id
      name
      short_name
      from_warehouse_id
      to_warehouse_id
      created_at
      updated_at
      deleted_at
      media {
        file_path
      }
      from_warehouse_ns_bin_id
      distribution_ns_class_id
    }
  }
`;

export type UpdateRetailerMutationVariables = {
  id: string
  updateRetailerInput: {
    name?: string
    short_name?: string
    from_warehouse_id?: number
    to_warehouse_id?: number
  }
};

export type UpdateRetailerMutation = {
  updateRetailer: RetailerSummary
};

export const UPDATE_RETAILER: TypedDocumentNode<
  UpdateRetailerMutation,
  UpdateRetailerMutationVariables
> = gql`
  mutation updateRetailer($id: ID!, $updateRetailerInput: UpdateRetailerInput!) {
    updateRetailer(id: $id, updateRetailerInput: $updateRetailerInput) {
      retailer_id
      name
      short_name
      from_warehouse_id
      to_warehouse_id
      created_at
      updated_at
    }
  }
`;

export type UpdateRetailerImageMutationVariables = {
  addRetailerImageInput: {
    retailerId: string
    imageData: string
    mimeType: string
  }
};

export type UpdateRetailerImageMutation = {
  addRetailerImage: {
    id: string
  }
};

export const UPDATE_RETAILER_IMAGE: TypedDocumentNode<
  UpdateRetailerImageMutation,
  UpdateRetailerImageMutationVariables
> = gql`
  mutation Mutation($addRetailerImageInput: AddRetailerImageInput!) {
    addRetailerImage(addRetailerImageInput: $addRetailerImageInput) {
      id
    }
  }`;

export type GetRetailerByIdQueryVariables = {
  id: string
};

export type GetRetailerByIdQuery = {
  retailer: RetailerSummary
};

export const GET_RETAILER_BY_ID: TypedDocumentNode<
  GetRetailerByIdQuery,
  GetRetailerByIdQueryVariables
> = gql`
  query retailer($id: ID!) {
    retailer(id: $id) {
      retailer_id
      name
      short_name
      from_warehouse_id
      to_warehouse_id
      created_at
      updated_at
      deleted_at
      media {
        file_path
      }
      from_warehouse_ns_bin_id
      distribution_ns_class_id
    }
  }
`;

export type GetRetailerProductsQueryVariables = {
  params: {
    limit?: number
    page?: number
    search?: string
    brand?: number
    status?: string
  }
};

export type GetRetailerProductsQuery = {
  getProducts: {
    current: number
    total: number
    perPage: number
    from: number
    to: number
    data: Array<{
      id: string
      label: string
      sku: string
      status: string
      attributes: string | null
      brand_id: number
    }>
    previous: number | null
    next: number | null
  }
};

export const GET_RETAILER_PRODUCTS: TypedDocumentNode<
  GetRetailerProductsQuery,
  GetRetailerProductsQueryVariables
> = gql`
  query getProductsQuery($params: ParamsInput!) {
    getProducts(params: $params) {
      current
      total
      perPage
      from
      to
      data {
        id
        label
        sku
        status
        attributes
        brand_id
      }
      previous
      next
    }
  }
`;

export type GetRetailerProductCasePackQtyAttributeQueryVariables = {
  id: string
};

export type GetRetailerProductCasePackQtyAttributeQuery = {
  getProductCasePackQtyAttribute: string | null
};

export const GET_RETAILER_PRODUCT_CASE_PACK_QTY_ATTRIBUTE: TypedDocumentNode<
  GetRetailerProductCasePackQtyAttributeQuery,
  GetRetailerProductCasePackQtyAttributeQueryVariables
> = gql`
  query getProductCasePackQtyAttribute($id: ID!) {
    getProductCasePackQtyAttribute(id: $id)
  }
`;

export type SetRetailerProductCasePackQtyAttributeMutationVariables = {
  id: string
  fieldName: string
  productId: string
};

export type SetRetailerProductCasePackQtyAttributeMutation = {
  setProductCasePackQtyAttribute: boolean
};

export const SET_RETAILER_PRODUCT_CASE_PACK_QTY_ATTRIBUTE: TypedDocumentNode<
  SetRetailerProductCasePackQtyAttributeMutation,
  SetRetailerProductCasePackQtyAttributeMutationVariables
> = gql`
  mutation setProductCasePackQtyAttribute($id: ID!, $fieldName: String!, $productId: String!) {
    setProductCasePackQtyAttribute(id: $id, fieldName: $fieldName, productId: $productId)
  }
`;

type RetailerLabel = {
  retailer_id: string
  label_id: string
  label: {
    id: string
    name: string
    type: string
    program: string
    title: string
    created_at: string
    updated_at: string
  } | null
  retailer: RetailerSummary | null
};

export type GetRetailerLabelsQueryVariables = {
  retailerId: string
};

export type GetRetailerLabelsQuery = {
  retailerLabels: RetailerLabel[]
};

export const GET_RETAILER_LABELS: TypedDocumentNode<
  GetRetailerLabelsQuery,
  GetRetailerLabelsQueryVariables
> = gql`
  query retailerLabels($retailerId: ID!) {
    retailerLabels(retailerId: $retailerId){
      retailer_id
      label_id
      label{
        id
        name
        type
        program
        title
        created_at
        updated_at
      }
      retailer{
        retailer_id
        name
        short_name
        created_at
        updated_at
        deleted_at
      }
    }
  }
`;

export type GetRetailerAvailableLabelsQueryVariables = {
  retailerId: string
};

export type GetRetailerAvailableLabelsQuery = {
  retailerAvailableLabels: Array<{
    id: string
    name: string
    type: string
    program: string
    title: string
    created_at: string
    updated_at: string
  }>
};

export const GET_RETAILER_AVAILABLE_LABELS: TypedDocumentNode<
  GetRetailerAvailableLabelsQuery,
  GetRetailerAvailableLabelsQueryVariables
> = gql`
  query retailerAvailableLabels($retailerId: ID!) {
    retailerAvailableLabels(retailerId: $retailerId){
      id
      name
      type
      program
      title
      created_at
      updated_at
    }
  }
`;

export type AddRetailerLabelMutationVariables = {
  createRetailerLabelInput: {
    retailer_id: string
    label_id: string
  }
};

export type AddRetailerLabelMutation = {
  addRetailerLabel: {
    retailer_id: string
    label_id: string
  }
};

export const ADD_RETAILER_LABEL: TypedDocumentNode<
  AddRetailerLabelMutation,
  AddRetailerLabelMutationVariables
> = gql`
  mutation addRetailerLabel($createRetailerLabelInput: CreateRetailerLabelInput!) {
    addRetailerLabel(createRetailerLabelInput: $createRetailerLabelInput){
      retailer_id
      label_id
    }
  }
`;

export type RemoveRetailerLabelMutationVariables = {
  removeRetailerLabelInput: {
    retailerId: string
    labelId: string
  }
};

export type RemoveRetailerLabelMutation = {
  removeRetailerLabel: RetailerLabel
};

export const REMOVE_RETAILER_LABEL: TypedDocumentNode<
  RemoveRetailerLabelMutation,
  RemoveRetailerLabelMutationVariables
> = gql`
  mutation removeRetailerLabel($removeRetailerLabelInput: RemoveRetailerLabelInput!) {
    removeRetailerLabel(removeRetailerLabelInput: $removeRetailerLabelInput){
      retailer_id
      label_id
      label{
        id
        name
        type
        program
        title
        created_at
        updated_at
      }
      retailer{
        retailer_id
        name
        short_name
        created_at
        updated_at
        deleted_at
      }
    }
  }
`;

export type CreateRetailerMutationVariables = {
  createRetailerInput: {
    name: string
    short_name: string
    from_warehouse_id?: number
    to_warehouse_id?: number
  }
};

export type CreateRetailerMutation = {
  createRetailer: {
    retailer_id: string
    name: string
    short_name: string
    from_warehouse_id: number | null
    to_warehouse_id: number | null
  }
};

export const CREATE_RETAILER: TypedDocumentNode<
  CreateRetailerMutation,
  CreateRetailerMutationVariables
> = gql`
  mutation CreateRetailer($createRetailerInput: CreateRetailerInput!) {
    createRetailer(createRetailerInput: $createRetailerInput) {
      retailer_id
      name
      short_name
      from_warehouse_id
      to_warehouse_id
    }
  }
`;

export type GetNetsuiteLocationIdsQuery = {
  netSuiteLocationIds: Array<{
    id: string
    fullname: string
    name: string
  }>
};

export const GET_NETSUITE_LOCATION_IDS: TypedDocumentNode<GetNetsuiteLocationIdsQuery> = gql`
  query netSuiteLocationIds {
    netSuiteLocationIds {
      id
      fullname
      name
    }
  }
`;

export type GetNetsuiteClassesQuery = {
  netSuiteClasses: Array<{
    id: string
    name: string
    fullname: string
  }>
};

export const GET_NETSUITE_CLASSES: TypedDocumentNode<GetNetsuiteClassesQuery> = gql`
  query netSuiteClasses {
    netSuiteClasses {
      id
      name
      fullname
    }
  }
`;
